import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Database } from 'lucide-react';
import './PrivacyPolicy.css';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token_akzicao') || sessionStorage.getItem('token_akzicao');
  const estaAutenticado = !!token;

  return (
    <main className="workspace-page privacidade-public-page">
      <header className="privacidade-hero">
        <span className="privacidade-kicker"><Database size={16} /> Legal & Conformidade</span>
        <h1>Política de Privacidade</h1>
        <p>Esta política reflete de forma transparente a recolha, tratamento e salvaguarda de dados reais na plataforma Akzição V1.</p>
      </header>

      <div className="privacidade-container">
        {!estaAutenticado && (
          <button type="button" className="botao botao-secundario voltar-login-btn" onClick={() => navigate('/login')}>
            ← Voltar para o Login
          </button>
        )}

        <article className="privacidade-content">
          <p className="privacidade-updated">Última atualização: 14 de julho de 2026</p>

          <section className="legal-section">
            <h2>1. Identificação da Akzição</h2>
            <p>A Akzição V1 é uma plataforma digital disponibilizada como Software as a Service (SaaS), concebida para a modernização e otimização de fluxos operacionais de procurement corporativo, desenvolvida e gerida pelo Estúdio Visão Ikigai.</p>
          </section>

          <section className="legal-section">
            <h2>2. Âmbito da Política</h2>
            <p>Esta política aplica-se exclusivamente aos dados tratados nas versões Web e Android da plataforma Akzição V1. Ela regula as informações fornecidas por utilizadores das equipas internas das empresas contratantes (Tenants), administradores e fornecedores comerciais.</p>
          </section>

          <section className="legal-section">
            <h2>3. Dados que podem ser tratados</h2>
            <p>De acordo com o mapeamento e implementação real do sistema, a plataforma recolhe e processa as seguintes categorias de dados:</p>
            <ul className="privacidade-list">
              <li><strong>Dados Identificativos e de Perfil:</strong> Nome completo, endereço de correio eletrónico (e-mail) e credenciais de acesso.</li>
              <li><strong>Dados de Contacto Corporativo:</strong> Números de telefone, ligações associadas ao WhatsApp, e-mail operacional e nome do contacto do responsável designado.</li>
              <li><strong>Dados de Localização Organizacional:</strong> Província, município, endereço comercial completo e referências físicas declaradas no perfil operacional.</li>
              <li><strong>Dados Fiscais e Societários:</strong> NIF (Número de Identificação Fiscal) ou CNPJ da entidade empresarial.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. Como os dados são utilizados</h2>
            <p>Os dados tratados destinam-se estritamente à execução e operacionalização das funcionalidades do ecossistema, incluindo:</p>
            <ul className="privacidade-list">
              <li>Verificação de identidade e controle de sessões ativas;</li>
              <li>Encaminhamento de requisições de compra e processamento de propostas comerciais;</li>
              <li>Auditoria retroativa e rastreabilidade de decisões administrativas através da Matriz de Alçada;</li>
              <li>Envio de comunicações informativas internas, avisos organizacionais e avisos urgentes no painel.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. Base e Finalidade do Tratamento</h2>
            <p>O tratamento de dados assenta na necessidade de execução de diligências pré-contratuais e contratuais entre as empresas operantes e os seus fornecedores homologados, assegurando a integridade e a validade jurídica de compras corporativas.</p>
          </section>

          <section className="legal-section">
            <h2>6. Dados relacionados a Contas</h2>
            <p>A plataforma armazena o histórico do perfil do utilizador (Solicitante, Comprador, Aprovador, Fiel de Armazém, Gerente de Empresa ou Super Admin), incluindo registos de data e hora do último acesso, status da conta e tokens de sessão ativa associados.</p>
          </section>

          <section className="legal-section">
            <h2>7. Dados relacionados a Empresas e Fornecedores</h2>
            <p>Para empresas e fornecedores submetidos à plataforma, os dados de faturamento, logotipos e dados operacionais de contacto permanecem isolados logicamente através de uma arquitetura multi-tenant, garantindo que os dados de uma organização não sejam acessíveis por terceiros não vinculados.</p>
          </section>

          <section className="legal-section">
            <h2>8. Dados das Operações de Procurement</h2>
            <p>A plataforma processa dados de requisições de compras (títulos, descrições, estimativas de custos, departamentos solicitantes), propostas financeiras de fornecedores (preços, termos técnicos, prazos de entrega) e comentários/votos emitidos por aprovadores habilitados.</p>
          </section>

          <section className="legal-section">
            <h2>9. Documentos e Ficheiros enviados</h2>
            <p>A Akzição armazena ficheiros enviados voluntariamente pelos utilizadores para fins de conformidade documental, tais como comprovativos de identidade pessoal, alvarás comerciais ou certidões fiscais da empresa nas candidaturas públicas, bem como faturas e comprovativos bancários de pagamento de subscrições e transações. Estes ficheiros são processados de forma codificada em formato seguro na base de dados.</p>
          </section>

          <section className="legal-section">
            <h2>10. Autenticação e Segurança</h2>
            <p>O acesso à plataforma é protegido por autenticação baseada em tokens JWT (JSON Web Tokens). Todas as palavras-passe (senhas) fornecidas passam por um processo de hashing seguro e irreversível no servidor antes do armazenamento, impedindo a sua visualização legível sob qualquer pretexto.</p>
          </section>

          <section className="legal-section">
            <h2>11. Armazenamento de Dados</h2>
            <p>Os dados de configuração da aplicação e os tokens JWT de autenticação móvel são armazenados localmente e de forma segura no dispositivo do utilizador utilizando o módulo nativo do <code>@capacitor/preferences</code>.</p>
          </section>

          <section className="legal-section">
            <h2>12. Partilha e Divulgação de Dados</h2>
            <p>Não existe qualquer venda, partilha comercial ou aluguer de dados pessoais com fins publicitários ou promocionais. Os dados são partilhados estritamente entre as partes envolvidas no mesmo fluxo de compras (a empresa que emitiu a requisição e o fornecedor que submeteu a respetiva proposta comercial comercialmente vinculada).</p>
          </section>

          <section className="legal-section">
            <h2>13. Serviços de Terceiros</h2>
            <p>A aplicação Akzição V1 comunica exclusivamente com a sua infraestrutura oficial direta (<code>api.akzicao.com</code>). Não estão integrados quaisquer SDKs de rastreamento de anúncios, pixels comportamentais de redes sociais, ferramentas externas de analytics (como Google Analytics ou Firebase Analytics) ou gateways internacionais não mapeados de processamento automatizado.</p>
          </section>

          <section className="legal-section">
            <h2>14. Retenção de Dados</h2>
            <p>Os dados operacionais e os registos de transações são conservados pelo tempo que for estritamente necessário para cumprir as finalidades contratuais e para dar resposta às obrigações legais, fiscais e contabilísticas da jurisdição em vigor.</p>
          </section>

          <section className="legal-section">
            <h2>15. Eliminação de Conta e Dados (Account Deletion)</h2>
            <p>Em total conformidade com as diretrizes de transparência do Google Play, qualquer utilizador autenticado possui o direito de submeter uma solicitação formal de eliminação de conta e dados a partir da área de configurações da aplicação (Account Deletion Request). </p>
            <p>Ao receber o pedido, os dados puramente de perfil pessoal e credenciais são eliminados ou anonimizados permanentemente dos sistemas operacionais ativos. Os dados históricos transacionais e fiscais de compras vinculados ao histórico corporativo da empresa contratante são preservados exclusivamente para fins de integridade de auditoria empresarial e conformidade legal.</p>
          </section>

          <section className="legal-section">
            <h2>16. Direitos do Utilizador</h2>
            <p>Os utilizadores registados podem exercer os seus direitos legais de acesso aos seus dados pessoais, retificação de informações incompletas ou incorretas, limitação do tratamento temporário e oposição ao tratamento operacional, utilizando os canais administrativos internos fornecidos pelo gerente da sua respetiva organização ou pelo suporte geral.</p>
          </section>

          <section className="legal-section">
            <h2>17. Padrões de Segurança</h2>
            <p>A equipa técnica aplica mecanismos modernos para assegurar o tráfego dos dados, incluindo comunicações encriptadas por protocolo TLS/HTTPS e canais WebSocket bidirecionais isolados. O utilizador reconhece que nenhum método de transmissão eletrónica ou armazenamento digital é 100% infalível e que a plataforma não assume garantias de segurança absoluta face a intrusões maliciosas extraordinárias.</p>
          </section>

          <section className="legal-section">
            <h2>18. Alterações à Política de Privacidade</h2>
            <p>Esta política poderá ser atualizada de tempos a tempos para refletir reajustes operacionais ou alterações na legislação de proteção de dados. Quaisquer modificações estruturais serão indicadas pela alteração da data de vigência e disponibilizadas para livre consulta pública.</p>
          </section>

          <section className="legal-section">
            <h2>19. Contacto</h2>
            <p>Para o esclarecimento de dúvidas sobre o tratamento de informações corporativas ou dados operacionais, contacte os canais de suporte oficiais disponibilizados na secção institucional da plataforma Akzição.</p>
          </section>

          <section className="legal-section">
            <h2>20. Entrada em Vigor</h2>
            <p>Esta Política de Privacidade entra em vigor e é integralmente aplicável a partir de 14 de julho de 2026, revogando entendimentos informais anteriores.</p>
          </section>
        </article>
      </div>
    </main>
  );
};

export default PrivacyPolicy;
